import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Spinner, Form, Button } from 'react-bootstrap';
import { useLanguage } from '../../context/LanguageContext';
import api, { getApiErrorMessage } from '../../services/api';
import { ReturnsTable } from '../../components/Returns';
import {
  ChatDataTable,
  ChatPagination,
  ChatPanel,
  ChatReportToolbar,
  ChatSubTabs,
  ChatSummaryCards,
  ChatTableToolbar,
} from '../ChatUiComponents';
import ReportCard from '../components/ReportCard';

const DEDUCTION_CHARGE_TYPES = [
  'receipt_warehouse',
  'receipt_pickup',
  'inspection_no_media',
  'inspection_with_media',
  'packaging_shipment',
  'storage_daily',
  'storage_warehouse',
  'prepare_return_to_seller',
  'disposal',
  'custom',
];

function deductionTypeLabel(t, code) {
  const key = `billing.type_${code}`;
  const label = t(key);
  if (label !== key) return label;
  if (code === 'custom') {
    const alt = t('billing.accountsCustomChargesTitle');
    if (alt !== 'billing.accountsCustomChargesTitle') return alt;
  }
  return code;
}

function todayMinus(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

function formatMoney(value) {
  if (value === null || value === undefined || value === '') return '—';
  return `¥${value}`;
}

function parseBalance(value) {
  const n = parseFloat(String(value ?? '').replace(',', '.'));
  return Number.isNaN(n) ? 0 : n;
}

export function DeductionsReport({ storeId, prefill, onPrefillConsumed }) {
  const { t, language } = useLanguage();
  const [from, setFrom] = useState(todayMinus(7));
  const [to, setTo] = useState(todayMinus(0));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState('');
  const [searchApplied, setSearchApplied] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [expandedType, setExpandedType] = useState(null);

  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    if (!prefill) return;
    if (prefill.from) setFrom(prefill.from);
    if (prefill.to) setTo(prefill.to);
    if (prefill.type != null) setTypeFilter(prefill.type);
    setPage(1);
    onPrefillConsumed?.();
  }, [prefill]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const res = await api.chatUiReportsDeductions({
        ...(from ? { date_from: from } : {}),
        ...(to ? { date_to: to } : {}),
        store_id: storeId || undefined,
        search: searchApplied || undefined,
        type: typeFilter || undefined,
        page,
        page_size: pageSize,
      });
      setData(res?.data ?? res);
      setExpandedType(null);
    } catch (err) {
      setLoadError(getApiErrorMessage(err, t('chatApp.aiError')));
    } finally {
      setLoading(false);
    }
  }, [from, to, storeId, searchApplied, typeFilter, page, pageSize, t]);

  useEffect(() => {
    load();
  }, [load]);

  const byType = (data?.by_type || []).filter((row) => row.count > 0);
  const rows = data?.rows || [];
  const totalCount = data?.count ?? 0;

  const typeOptions = useMemo(
    () => DEDUCTION_CHARGE_TYPES.map((code) => ({
      value: code,
      label: deductionTypeLabel(t, code),
    })),
    [t, language],
  );

  const summaryItems = data
    ? [
        { key: 'total', value: formatMoney(data.total_charges), label: t('chatApp.deductionsTotal') },
        {
          key: 'ops',
          value: data.operations_count ?? 0,
          label: t('chatApp.operationsCount'),
        },
        {
          key: 'types',
          value: data.charge_types_active ?? 0,
          label: t('chatApp.chargeTypes'),
        },
        {
          key: 'balance',
          value: formatMoney(data.store_balance ?? data.current_balance),
          label: data.store_balance != null
            ? (data.store_name || t('chatApp.storeBalance'))
            : t('chatApp.balanceTotal'),
          variant: parseBalance(data.store_balance ?? data.current_balance) < 0 ? 'danger' : '',
        },
      ]
    : [];

  const expandedBarcodes = useMemo(() => {
    if (!expandedType) return [];
    const row = byType.find((r) => r.type === expandedType);
    return row?.barcodes || [];
  }, [byType, expandedType]);

  const handleToolbarLoad = () => {
    setSearchApplied(search.trim());
    setPage(1);
  };

  const handleTypeChange = (value) => {
    setTypeFilter(value);
    setPage(1);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await api.chatUiReportsDeductionsExport({
        ...(from ? { date_from: from } : {}),
        ...(to ? { date_to: to } : {}),
        store_id: storeId || undefined,
        search: searchApplied || undefined,
        type: typeFilter || undefined,
        lang: language || 'en',
      });
      const blob = res?.data;
      if (!blob) throw new Error('No file received');
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `deductions_${from || 'all'}_${to || 'all'}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div>
      <ChatReportToolbar
        from={from}
        to={to}
        onFrom={(value) => {
          setFrom(value);
          setPage(1);
        }}
        onTo={(value) => {
          setTo(value);
          setPage(1);
        }}
        loading={loading}
        showLoadButton={false}
      />
      {(!from || !to) && (
        <p className="small text-muted mb-2">{t('chatApp.deductionsAllPeriodHint')}</p>
      )}

      {loadError && (
        <p className="small text-danger mb-2">{loadError}</p>
      )}

      {loading && !data && (
        <div className="text-center py-4">
          <Spinner animation="border" size="sm" />
        </div>
      )}

      {data && (
        <div className={loading ? 'chat-app-report-loading' : ''}>
          <ChatSummaryCards items={summaryItems} />

          <ChatPanel title={t('chatApp.detailsByType')}>
            <ChatDataTable
              columns={[
                { key: 'label', label: t('chatApp.chargeType') },
                { key: 'count', label: t('chatApp.count'), align: 'end', numeric: true },
                {
                  key: 'total',
                  label: t('chatApp.amount'),
                  align: 'end',
                  numeric: true,
                  render: (r) => formatMoney(r.total),
                },
                {
                  key: 'action',
                  label: '',
                  width: '6rem',
                  render: (r) =>
                    (r.barcodes || []).length > 0 ? (
                      <button
                        type="button"
                        className="chat-app-table-link"
                        onClick={() =>
                          setExpandedType(expandedType === r.type ? null : r.type)
                        }
                      >
                        {expandedType === r.type ? t('chatApp.hide') : t('chatApp.details')}
                      </button>
                    ) : null,
                },
              ]}
              rows={byType}
              getRowKey={(row) => row.type}
            />
            {expandedType && expandedBarcodes.length > 0 && (
              <div className="chat-app-nested-table mt-3">
                <ChatDataTable
                  columns={[
                    { key: 'barcode', label: t('chatApp.barcode') },
                    { key: 'count', label: t('chatApp.count'), align: 'end', numeric: true },
                    {
                      key: 'total',
                      label: t('chatApp.amount'),
                      align: 'end',
                      numeric: true,
                      render: (r) => formatMoney(r.total),
                    },
                  ]}
                  rows={expandedBarcodes}
                  getRowKey={(r) => `${expandedType}-${r.barcode}`}
                />
              </div>
            )}
          </ChatPanel>

          <ChatPanel
            title={t('chatApp.deductionsList')}
            actions={null}
          >
            <ChatTableToolbar
              search={search}
              onSearch={setSearch}
              onSearchSubmit={handleToolbarLoad}
              type={typeFilter}
              onType={handleTypeChange}
              typeOptions={typeOptions}
              onExport={handleExport}
              exporting={exporting}
            />
            <ChatDataTable
              columns={[
                { key: 'date', label: t('chatApp.date') },
                { key: 'type_label', label: t('billing.type') },
                { key: 'product', label: t('billing.product') },
                { key: 'barcode', label: t('chatApp.barcode') },
                { key: 'order_no', label: t('billing.orderNo') },
                {
                  key: 'amount',
                  label: t('chatApp.amount'),
                  align: 'end',
                  numeric: true,
                  render: (r) => formatMoney(r.amount),
                },
              ]}
              rows={rows}
              getRowKey={(r) => r.id}
            />
            <ChatPagination
              page={page}
              pageSize={pageSize}
              totalItems={totalCount}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(1);
              }}
            />
          </ChatPanel>
        </div>
      )}
    </div>
  );
}

export function SalesReport({ selectedStoreId }) {
  return (
    <ReturnsTable
      embeddedInChatApp
      activeTab="whSale"
      hideTabNavigation
      selectedStoreId={selectedStoreId}
    />
  );
}

const RETURN_SUB_TABS = [
  { id: 'inTransit', labelKey: 'returns.inTransit', countKey: 'in_transit' },
  { id: 'inProcessing', labelKey: 'returns.ordersInProcessing', countKey: 'in_processing' },
  { id: 'pendingDecision', labelKey: 'returns.ordersPendingDecision', countKey: 'pending_decision', highlight: true },
  { id: 'completed', labelKey: 'returns.completedOrders', countKey: 'completed' },
  { id: 'otherPvz', labelKey: 'returns.filterByOtherPvz', countKey: 'other_pvz_in_transit' },
];

export function ReturnsReport({ selectedStoreId, prefill, onPrefillConsumed }) {
  const { t } = useLanguage();
  const [subTab, setSubTab] = useState(() => prefill?.subTab || 'inTransit');
  const [tabCounts, setTabCounts] = useState({});

  useEffect(() => {
    if (!prefill) return;
    if (prefill.subTab) setSubTab(prefill.subTab);
    onPrefillConsumed?.();
  }, [prefill]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let cancelled = false;
    api
      .getReturnsStatistics(selectedStoreId ? { store_id: selectedStoreId } : {})
      .then((res) => {
        if (cancelled) return;
        const data = res?.data ?? res;
        setTabCounts(data && typeof data === 'object' ? data : {});
      })
      .catch(() => {
        if (!cancelled) setTabCounts({});
      });
    return () => { cancelled = true; };
  }, [selectedStoreId]);

  const tabs = RETURN_SUB_TABS.map((tab) => ({
    id: tab.id,
    label: t(tab.labelKey),
    count: tabCounts[tab.countKey] ?? 0,
    highlight: Boolean(tab.highlight),
  }));

  return (
    <div className="chat-app-returns-report">
      <ChatSubTabs tabs={tabs} active={subTab} onChange={setSubTab} />
      <ReturnsTable
        embeddedInChatApp
        activeTab={subTab}
        hideTabNavigation
        selectedStoreId={selectedStoreId}
      />
    </div>
  );
}

function yesterdayIso() {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().slice(0, 10);
}

export function DailyActivityReport({ storeId, stores, onNavigateDeductions, onNavigateReturns }) {
  const { t, language } = useLanguage();
  const [from, setFrom] = useState(yesterdayIso);
  const [to, setTo] = useState(yesterdayIso);
  const [selectedStore, setSelectedStore] = useState(storeId || '');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    setSelectedStore(storeId || '');
  }, [storeId]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.chatUiReportsDaily({
        date_from: from,
        date_to: to,
        store_id: selectedStore || undefined,
        lang: language,
      });
      setData(res?.data ?? res);
    } finally {
      setLoading(false);
    }
  }, [from, to, selectedStore, language]);

  useEffect(() => {
    load();
  }, [load]);

  const summary = data?.summary || {};
  const scopeLabel = data?.store_name || (selectedStore ? selectedStore : t('chatApp.dailyAllStores'));
  const reportCard = data?.report_card || null;

  const summaryItems = data && !reportCard
    ? [
        { key: 'charges', value: formatMoney(summary.total_charges), label: t('chatApp.deductionsTotal') },
        { key: 'ops', value: summary.operations_count ?? 0, label: t('chatApp.operationsCount') },
        {
          key: 'receipts',
          value: `${summary.receipts_count ?? 0} / ${formatMoney(summary.receipts_total)}`,
          label: t('chatApp.dailyReceipts'),
        },
        {
          key: 'disposal',
          value: `${summary.disposal_count ?? 0} / ${formatMoney(summary.disposal_total)}`,
          label: t('chatApp.dailyDisposal'),
        },
        {
          key: 'topups',
          value: `${summary.topups_count ?? 0} / ${formatMoney(summary.topups_total)}`,
          label: t('chatApp.dailyTopups'),
        },
        {
          key: 'balance',
          value: formatMoney(data.store_balance ?? data.current_balance),
          label: data.store_balance != null ? (data.store_name || t('chatApp.storeBalance')) : t('chatApp.balanceTotal'),
          variant: parseBalance(data.store_balance ?? data.current_balance) < 0 ? 'danger' : '',
        },
      ]
    : [];

  const handleExportPdf = async () => {
    setExporting(true);
    try {
      const blobRes = await api.chatUiReportsDailyExport({
        date_from: from,
        date_to: to,
        date: from,
        store_id: selectedStore || undefined,
        lang: language,
      });
      const blob = blobRes?.data ?? blobRes;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `daily_report_${from}_${to}_${selectedStore || 'all'}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  const storeOptions = [
    { value: '', label: t('chatApp.dailyAllStores') },
    ...(stores || []).map((s) => ({ value: s.store_id || '', label: s.name || s.store_id })),
  ];

  return (
    <div className="chat-app-daily-report">
      <div className="chat-app-toolbar mb-3">
        <div className="chat-app-toolbar-dates flex-wrap">
          <label className="chat-app-field-label">{t('chatApp.periodFrom')}</label>
          <Form.Control type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          <label className="chat-app-field-label">{t('chatApp.periodTo')}</label>
          <Form.Control type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          <label className="chat-app-field-label">{t('chatApp.store')}</label>
          <Form.Select
            value={selectedStore}
            onChange={(e) => setSelectedStore(e.target.value)}
            disabled={Boolean(storeId)}
          >
            {storeOptions.map((opt) => (
              <option key={opt.value || 'all'} value={opt.value}>{opt.label}</option>
            ))}
          </Form.Select>
          <Button size="sm" className="btn-gradient" onClick={load} disabled={loading}>
            {loading ? <Spinner size="sm" animation="border" /> : t('chatApp.load')}
          </Button>
          <Button size="sm" variant="outline-success" onClick={handleExportPdf} disabled={exporting || !data}>
            {exporting ? <Spinner size="sm" animation="border" /> : t('chatApp.downloadPdf')}
          </Button>
        </div>
        <div className="small text-muted mt-2">{scopeLabel}</div>
      </div>

      {loading && !data ? (
        <div className="text-center py-4"><Spinner animation="border" size="sm" /></div>
      ) : !data ? (
        <p className="text-muted small">{t('chatApp.noData')}</p>
      ) : reportCard ? (
        <ReportCard
          card={reportCard}
          onNavigateDeductions={onNavigateDeductions}
          onNavigateReturns={onNavigateReturns}
        />
      ) : (
        <>
          <ChatSummaryCards items={summaryItems} />

          {(data.by_type || []).length > 0 && (
            <ChatPanel title={t('chatApp.detailsByType')}>
              <ChatDataTable
                columns={[
                  { key: 'label', label: t('chatApp.chargeType') },
                  { key: 'count', label: t('chatApp.count'), align: 'end', numeric: true },
                  {
                    key: 'total',
                    label: t('chatApp.amount'),
                    align: 'end',
                    numeric: true,
                    render: (row) => formatMoney(row.total),
                  },
                ]}
                rows={data.by_type}
                emptyText={t('chatApp.noData')}
              />
            </ChatPanel>
          )}

          {(data.sample_charges || []).length > 0 && (
            <ChatPanel title={t('chatApp.dailyChargesList')}>
              <ChatDataTable
                columns={[
                  { key: 'date', label: t('chatApp.date') },
                  { key: 'type_label', label: t('chatApp.chargeType') },
                  { key: 'barcode', label: t('chatApp.barcode') },
                  {
                    key: 'amount',
                    label: t('chatApp.amount'),
                    align: 'end',
                    numeric: true,
                    render: (row) => formatMoney(row.amount),
                  },
                ]}
                rows={data.sample_charges}
                emptyText={t('chatApp.noData')}
              />
            </ChatPanel>
          )}

        </>
      )}
    </div>
  );
}
